import { supabase } from './client';
import { logSystemError, translateSystemError } from '@/lib/utils/errorTranslator';
import type { ServiceEventCompletion } from '@/types/serviceEvents';

// The tenant-scoped view exposes prod.service_events filtered to the caller's tenant.
// It lives in the public schema, so we use the main supabase client (the prod-schema
// client defaults to db.schema='prod' and would look in the wrong place).
const VIEW = 'service_events_tenant_filtered';

// PostgREST returns Postgres numeric/decimal columns as JSON strings. Coerce defensively
// so any downstream arithmetic (Σ hours_worked for KPI strip) doesn't silently break.
function toNumber(value: unknown): number | null {
  if (value === null || value === undefined) return null;
  const n = typeof value === 'number' ? value : parseFloat(String(value).trim());
  return Number.isFinite(n) ? n : null;
}

// Fields the iOS app accidentally serialized into service_notes on older completions.
// If a notes value is a JSON object containing any of these, it's the polluted payload
// and not real tech notes — we strip it so it never reaches the UI.
const IOS_PAYLOAD_KEY_SIGNATURE = new Set([
  'technician_signature_url',
  'dealer_signature_url',
  'completion_photos',
  'vehicle_mileage',
  'dealer_signee_name',
  'calculated_hours',
  'hours_worked'
]);

// Guardrail: detect and drop service_notes values that are the iOS completion payload
// blob mis-written as notes (legacy data quality issue). Real notes pass through unchanged.
//
// Detection is intentionally specific — a value is only dropped if it parses as a JSON
// OBJECT whose keys overlap with the known payload signature. Legitimate notes that
// happen to start with `{` (e.g. "{handwritten}") don't trigger.
function sanitizeServiceNotes(notes: string | null | undefined): string | null {
  if (notes === null || notes === undefined) return null;
  const trimmed = notes.trim();
  if (trimmed === '') return null;
  // Fast path: anything not starting with `{` cannot be a JSON object payload.
  if (!trimmed.startsWith('{')) return notes;
  try {
    const parsed: unknown = JSON.parse(trimmed);
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      const keys = Object.keys(parsed as Record<string, unknown>);
      const looksLikePayload = keys.some(k => IOS_PAYLOAD_KEY_SIGNATURE.has(k));
      if (looksLikePayload) return null;
    }
  } catch {
    // Not valid JSON — definitely real notes; leave alone.
  }
  return notes;
}

function normalizeRow(row: ServiceEventCompletion): ServiceEventCompletion {
  return {
    ...row,
    hours_worked: toNumber(row.hours_worked),
    calculated_hours: toNumber(row.calculated_hours),
    service_notes: sanitizeServiceNotes(row.service_notes)
  };
}

// Batch IDs so the .in('assignment_id', [...]) URL stays under PostgREST limits.
// Mirrors the batchArray pattern in routeTracking.ts.
function chunk<T>(items: T[], size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < items.length; i += size) out.push(items.slice(i, i + size));
  return out;
}

export async function getServiceEventByAssignmentId(
  assignmentId: string
): Promise<ServiceEventCompletion | null> {
  const { data, error } = await supabase
    .from(VIEW)
    .select('*')
    .eq('assignment_id', assignmentId)
    .eq('status', 'completed')
    .maybeSingle();

  if (error) {
    logSystemError(error, 'fetch service_event by assignment');
    return null;
  }
  return data ? normalizeRow(data as unknown as ServiceEventCompletion) : null;
}

export async function getServiceEventsByAssignmentIds(
  assignmentIds: string[]
): Promise<Map<string, ServiceEventCompletion>> {
  const out = new Map<string, ServiceEventCompletion>();
  if (assignmentIds.length === 0) return out;

  for (const ids of chunk(assignmentIds, 100)) {
    const { data, error } = await supabase
      .from(VIEW)
      .select('*')
      .in('assignment_id', ids)
      .eq('status', 'completed');

    if (error) {
      logSystemError(error, 'batch fetch service_events');
      throw translateSystemError(error, 'batch fetch service_events');
    }

    (data ?? []).forEach(raw => {
      const row = normalizeRow(raw as unknown as ServiceEventCompletion);
      if (row.assignment_id) out.set(row.assignment_id, row);
    });
  }
  return out;
}
